import React, { useEffect, useState, useCallback } from "react";
import { ExternalLink, Check, X, Trash2, Inbox, RefreshCw } from "lucide-react";
import { api, etTime, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS_STYLE = {
  new: "bg-amber-100 text-amber-700",
  added: "bg-green-100 text-green-700",
  declined: "bg-gray-200 text-gray-600",
};
const FILTERS = ["all", "new", "added", "declined"];

export default function AdminProductRequests() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? { status: filter } : {};
      const { data } = await api.get("/admin/product-requests", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (r, status) => {
    try {
      await api.patch(`/admin/product-requests/${r.id}`, { status });
      toast.success(status === "added" ? "Marked as added" : status === "declined" ? "Declined" : "Reopened");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (r) => {
    if (!window.confirm("Delete this request permanently?")) return;
    try {
      await api.delete(`/admin/product-requests/${r.id}`);
      toast.success("Deleted");
      setItems((list) => list.filter((x) => x.id !== r.id));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text).then(() => toast.success("Link copied")).catch(() => {});
  };

  return (
    <div data-testid="admin-product-requests">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-navy">Product Requests</h1>
          <p className="text-ink/60 mt-1">Links customers want you to add to the store.</p>
        </div>
        <button data-testid="pr-refresh" onClick={load} className="inline-flex items-center gap-2 rounded-md border border-navy/20 bg-white px-4 py-2 text-sm font-bold text-navy hover:border-brick">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="mt-5 flex items-center gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f}
            data-testid={`pr-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`rounded-full px-4 py-2 text-sm font-bold capitalize transition-colors ${filter === f ? "bg-brick text-white" : "bg-white border border-navy/20 text-navy hover:border-brick"}`}
          >
            {f}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="mt-12 text-center text-navy/50">Loading requests…</p>
      ) : items.length === 0 ? (
        <div data-testid="pr-empty" className="mt-12 text-center bg-white rounded-xl border border-navy/15 p-12">
          <Inbox className="w-12 h-12 text-navy/20 mx-auto" />
          <p className="font-display text-2xl text-navy mt-4">No requests here</p>
          <p className="text-ink/60 mt-2">When a customer submits a product link, it will show up here.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {items.map((r) => (
            <div key={r.id} data-testid={`pr-item-${r.id}`} className="bg-white rounded-xl border border-navy/15 p-5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <span className={`text-xs font-bold rounded-full px-2.5 py-1 capitalize ${STATUS_STYLE[r.status] || "bg-gray-100 text-gray-600"}`}>{r.status}</span>
                <span className="text-xs text-ink/50">{etTime(r.created_at)}</span>
              </div>

              <div className="mt-3">
                <a
                  href={r.product_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`pr-link-${r.id}`}
                  className="inline-flex items-center gap-2 text-navy font-bold break-all hover:text-brick"
                >
                  <ExternalLink className="w-4 h-4 shrink-0" /> {r.product_url}
                </a>
                <button onClick={() => copy(r.product_url)} className="ml-3 text-xs font-bold text-brick hover:underline">Copy</button>
              </div>

              {r.note && <p className="mt-2 text-sm text-ink/80"><span className="font-bold">Note:</span> {r.note}</p>}
              {(r.customer_name || r.customer_email) && (
                <p className="mt-1 text-sm text-ink/60">
                  From: {r.customer_name || "Anonymous"}{r.customer_email ? ` · ${r.customer_email}` : ""}
                </p>
              )}

              <div className="mt-4 flex items-center gap-2 flex-wrap">
                {r.status !== "added" && (
                  <button data-testid={`pr-added-${r.id}`} onClick={() => setStatus(r, "added")} className="inline-flex items-center gap-1.5 rounded-md bg-green-600 text-white text-sm font-bold px-3 py-2 hover:bg-green-700">
                    <Check className="w-4 h-4" /> Mark added
                  </button>
                )}
                {r.status === "added" && (
                  <button data-testid={`pr-reopen-${r.id}`} onClick={() => setStatus(r, "new")} className="inline-flex items-center gap-1.5 rounded-md border border-navy/20 text-navy text-sm font-bold px-3 py-2 hover:border-brick">
                    <RefreshCw className="w-4 h-4" /> Reopen
                  </button>
                )}
                {r.status !== "declined" && (
                  <button data-testid={`pr-decline-${r.id}`} onClick={() => setStatus(r, "declined")} className="inline-flex items-center gap-1.5 rounded-md border border-navy/20 text-ink/70 text-sm font-bold px-3 py-2 hover:border-brick">
                    <X className="w-4 h-4" /> Decline
                  </button>
                )}
                <button data-testid={`pr-delete-${r.id}`} onClick={() => remove(r)} className="inline-flex items-center gap-1.5 rounded-md text-red-600 text-sm font-bold px-3 py-2 hover:bg-red-50 ml-auto">
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
